import { useMutation, useQuery } from '@apollo/client'
import { Form, ModalForm, PasswordInput, TextArea, useForm } from '@faceup/form'
import { UntitledIcon } from '@faceup/icons'
import { ulClockRewind } from '@faceup/icons/ulClockRewind'
import { Card, ContentLayout } from '@faceup/ui'
import {
  Alert,
  Button,
  List,
  Modal,
  Select,
  Switch,
  Typography,
  useNotification,
} from '@faceup/ui-base'
import {
  convertLanguageToLanguageName,
  DEFAULT_LANGUAGE,
  Language,
  processAiHotlineInstructions,
} from '@faceup/utils'
import { type InferType, yup } from '@faceup/validation'
import { useState } from 'react'
import { graphql } from '../__generated__'

const { Title, Text } = Typography

const PAGE_PASSWORD = 'faceup-ai-hotline'

const query = {
  current: graphql(`
    query AiHotlineSystemInstructionsPageQuery {
      aiHotlineCurrentSystemInstructions {
        id
        systemInstructions
        createdAt
      }
    }
  `),
  history: graphql(`
    query AiHotlineSystemInstructionsHistoryQuery {
      aiHotlineSystemInstructionsHistory {
        id
        systemInstructions
        createdAt
      }
    }
  `),
}

const mutations = {
  update: graphql(`
    mutation UpdateAiHotlineSystemInstructionsMutation(
      $input: UpdateAiHotlineSystemInstructionsInput!
    ) {
      updateAiHotlineSystemInstructions(input: $input) {
        success
      }
    }
  `),
}

const schema = {
  password: yup.object().shape({
    password: yup.string().required(),
  }),
  systemInstructions: yup.object().shape({
    systemInstructions: yup.string().required(),
  }),
}

const AiHotlineSystemInstructions = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [previewSource, setPreviewSource] = useState<{
    instructions: string
    title: string
  } | null>(null)
  const [previewLanguage, setPreviewLanguage] = useState<Language>(DEFAULT_LANGUAGE)
  const [previewEmergency, setPreviewEmergency] = useState(false)
  const [previewContent, setPreviewContent] = useState('')

  const { data, loading } = useQuery(query.current)
  const { data: historyData, refetch } = useQuery(query.history)
  const notification = useNotification()

  const [updateInstructions] = useMutation(mutations.update, {
    refetchQueries: [{ query: query.history }, { query: query.current }],
  })

  const form = useForm({
    schema: schema.systemInstructions,
    afterSubmit: 'persistValues',
    values: {
      systemInstructions: data?.aiHotlineCurrentSystemInstructions?.systemInstructions ?? '',
    },
  })

  const passwordForm = useForm({
    schema: schema.password,
    afterSubmit: 'persistValues',
    defaultValues: { password: '' },
  })

  const currentInstructions = data?.aiHotlineCurrentSystemInstructions?.systemInstructions ?? ''

  const handlePreviewClick = () => {
    const content = processAiHotlineInstructions(
      currentInstructions,
      previewLanguage,
      previewEmergency
    )
    setPreviewContent(content)
    setPreviewSource({
      instructions: currentInstructions,
      title: 'Current Instructions',
    })
  }

  const handleViewHistoryItem = (item: {
    id: string
    systemInstructions: string
    createdAt: string
  }) => {
    const content = processAiHotlineInstructions(
      item.systemInstructions,
      previewLanguage,
      previewEmergency
    )
    setPreviewContent(content)
    setPreviewSource({
      instructions: item.systemInstructions,
      title: `History - ${new Date(item.createdAt).toLocaleString()}`,
    })
  }

  return (
    <ContentLayout header={<ContentLayout.Header title='AI Hotline System Instructions' />}>
      <ModalForm
        form={passwordForm}
        open={!isAuthenticated}
        onSubmit={values => {
          if (values.password === PAGE_PASSWORD) {
            setIsAuthenticated(true)
            return true
          }
          passwordForm.setError('password', { message: 'Incorrect password' })
          return false
        }}
        title='Enter Password'
        submitButtonText='save'
        isCancelButtonHidden
      >
        <PasswordInput
          control={passwordForm.control}
          name='password'
          label='Password'
          placeholder='Enter password to access this page'
        />
      </ModalForm>

      <Alert
        message='Instructions Guide'
        className='mb-4'
        description={
          <div>
            <Text strong>Available Variables:</Text>
            <ul>
              <li>
                <Text code>{'{LANGUAGE}'}</Text> - Replaced with the detected language name
              </li>
            </ul>
            <Text strong>Conditional Blocks:</Text>
            <ul>
              <li>
                <Text code>{'<!-- IF_EMERGENCY_NUMBER_EXISTS -->'}</Text> ...{' '}
                <Text code>{'<!-- END_IF_EMERGENCY_NUMBER_EXISTS -->'}</Text> - Content shown only
                when emergency number is configured
              </li>
              <li>
                <Text code>{'<!-- IF_EMERGENCY_NUMBER_MISSING -->'}</Text> ...{' '}
                <Text code>{'<!-- END_IF_EMERGENCY_NUMBER_MISSING -->'}</Text> - Content shown only
                when emergency number is NOT configured
              </li>
            </ul>
          </div>
        }
        type='info'
      />

      <Card loading={loading} className='mb-4'>
        <Card.Header>System Instructions</Card.Header>
        <Card.Content>
          <Form
            customSubmitButtonText='Save Instructions'
            customSubmitSuccessText='Instructions updated successfully'
            form={form}
            onSubmit={async (values: InferType<typeof schema.systemInstructions>) => {
              const result = await updateInstructions({
                variables: {
                  input: {
                    systemInstructions: values.systemInstructions,
                    shouldSynchronizeAllRegions: true,
                  },
                },
              })

              if (!result.data?.updateAiHotlineSystemInstructions?.success) {
                notification.error({
                  message: 'Error',
                  description: 'Failed to update system instructions. Please try again.',
                })
                return false
              }

              return true
            }}
          >
            <TextArea
              control={form.control}
              name='systemInstructions'
              rows={25}
              placeholder='Enter the system instructions for the AI Voice Agent...'
              className='font-mono'
            />

            <Button
              type='primary'
              onClick={handlePreviewClick}
              disabled={loading || !currentInstructions}
            >
              Preview & Test
            </Button>
          </Form>
        </Card.Content>
      </Card>

      <Card>
        <Card.Header
          extra={
            <Button icon={<UntitledIcon icon={ulClockRewind} />} onClick={() => refetch()}>
              Refresh
            </Button>
          }
        >
          History
        </Card.Header>

        <Card.Content>
          <List
            dataSource={
              historyData?.aiHotlineSystemInstructionsHistory?.filter(
                (item): item is NonNullable<typeof item> => item !== null
              ) ?? []
            }
            loading={!historyData}
            renderItem={item => (
              <List.Item
                actions={[
                  <Button
                    key='view'
                    type='link'
                    onClick={() =>
                      handleViewHistoryItem({
                        id: item.id,
                        systemInstructions: item.systemInstructions,
                        createdAt: item.createdAt,
                      })
                    }
                  >
                    Preview & Test
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={new Date(item.createdAt).toLocaleString()}
                  description={`${item.systemInstructions.length} characters`}
                />
              </List.Item>
            )}
          />
        </Card.Content>
      </Card>

      <Modal
        title={previewSource?.title ?? 'Preview & Test Instructions'}
        open={Boolean(previewSource)}
        onClose={() => setPreviewSource(null)}
        width={1000}
        footer={<Button onClick={() => setPreviewSource(null)}>Close</Button>}
      >
        <div>
          <Title level={5} className='mb-3'>
            Test Variables
          </Title>
          <div className='flex flex-col gap-4 w-full'>
            <div>
              <Text strong>Language Variable:</Text>
              <Select
                value={previewLanguage}
                onChange={value => {
                  setPreviewLanguage(value)
                  if (previewSource) {
                    const newContent = processAiHotlineInstructions(
                      previewSource.instructions,
                      value,
                      previewEmergency
                    )
                    setPreviewContent(newContent)
                  }
                }}
                className='mt-2'
                options={Object.values(Language).map(lang => ({
                  label: convertLanguageToLanguageName(lang),
                  value: lang,
                }))}
                showSearch
                placeholder='Select a language'
              />
              <Text type='secondary' className='text-xs mt-1 block'>
                This will replace <Text code>{'{LANGUAGE}'}</Text> in the instructions
              </Text>
            </div>
            <div>
              <div className='flex items-center gap-2'>
                <Text strong>Emergency Number Configured:</Text>
                <Switch
                  checked={previewEmergency}
                  onChange={checked => {
                    setPreviewEmergency(checked)
                    if (previewSource) {
                      const newContent = processAiHotlineInstructions(
                        previewSource.instructions,
                        previewLanguage,
                        checked
                      )
                      setPreviewContent(newContent)
                    }
                  }}
                />
                <Text type='secondary'>{previewEmergency ? 'Yes' : 'No'}</Text>
              </div>
              <Text type='secondary' className='text-xs mt-1 block'>
                Controls whether content between{' '}
                <Text code>{'<!-- IF_EMERGENCY_NUMBER_EXISTS -->'}</Text> and{' '}
                <Text code>{'<!-- END_IF_EMERGENCY_NUMBER_EXISTS -->'}</Text> is shown (and hides{' '}
                <Text code>{'<!-- IF_EMERGENCY_NUMBER_MISSING -->'}</Text> ...{' '}
                <Text code>{'<!-- END_IF_EMERGENCY_NUMBER_MISSING -->'}</Text> blocks)
              </Text>
            </div>
          </div>

          <div className='mt-4'>
            <Title level={5} className='mb-3'>
              Preview Output
            </Title>
            <pre className='font-mono border-1 rounded-xl p-4 max-h-[40vh] overflow-y-auto break-words whitespace-pre-wrap border-[#cdddee]'>
              {previewContent}
            </pre>
          </div>
        </div>
      </Modal>
    </ContentLayout>
  )
}

export default AiHotlineSystemInstructions
